// Hoja "Cronológico" del Reporte de Movimientos de Plazas (ficha 186): una fila por empleado (o
// becario / honorario asimilado) y, hacia la derecha, sus asignaciones y desasignaciones del
// periodo en orden de fecha, un bloque de columnas por movimiento.
import moment from 'moment';
import { aplicarEncabezado, escribirLineasEncabezado, fechaDMY } from 'utils/excelReportes';

const CAMPOS_MOVIMIENTO = [
  { header: 'Fecha', key: 'fecha_movimiento', width: 12 },
  { header: 'Movimiento', key: 'movimiento', width: 15 },
  { header: 'Plaza', key: 'plaza', width: 30 },
  { header: 'Puesto', key: 'puesto', width: 28 },
  { header: 'Área', key: 'area', width: 34 },
  { header: 'Tipo de Nómina', key: 'tipo_de_nomina_empleado', width: 20 },
  { header: 'Motivo', key: 'motivo', width: 30 },
];

const COLOR_ASIGNACION = 'FFE2EFDA';
const COLOR_DESASIGNACION = 'FFFCE4E4';

const fechaComoNumero = (valor) => (valor ? moment(valor).valueOf() : 0);

// Mismo día: primero la desasignación y luego la nueva asignación (cambio de puesto/plaza),
// salvo que sean la asignación y la desasignación de la MISMA plaza, que van en ese orden.
const compararMovimientos = (a, b) => {
  const diff = fechaComoNumero(a.fecha_movimiento) - fechaComoNumero(b.fecha_movimiento);
  if (diff !== 0) return diff;
  if (a.adp_id === b.adp_id) return a.esAsignacion ? -1 : 1;
  if (a.esAsignacion !== b.esAsignacion) return a.esAsignacion ? 1 : -1;
  return a.adp_id - b.adp_id;
};

/**
 * Agrupa las asignaciones y desasignaciones por sujeto, ordenadas cronológicamente.
 * @returns {{ empleado: string, tipo_de_sujeto: string, movimientos: object[] }[]}
 */
export const agruparMovimientosPorSujeto = ({ asignaciones = [], desasignaciones = [] }) => {
  const porSujeto = new Map();
  const agregar = (fila, esAsignacion) => {
    const llave = `${fila.tipo_de_sujeto}|${fila.sujeto_id}`;
    if (!porSujeto.has(llave)) {
      porSujeto.set(llave, {
        empleado: fila.empleado, tipo_de_sujeto: fila.tipo_de_sujeto, movimientos: [],
      });
    }
    porSujeto.get(llave).movimientos.push({
      ...fila,
      adp_id: fila.id,
      esAsignacion,
      movimiento: esAsignacion ? 'ASIGNACIÓN' : fila.tipo_de_movimiento,
    });
  };
  asignaciones.forEach((f) => agregar(f, true));
  desasignaciones.forEach((f) => agregar(f, false));

  return [...porSujeto.values()]
    .map((s) => ({ ...s, movimientos: s.movimientos.sort(compararMovimientos) }))
    .sort((a, b) => String(a.empleado).localeCompare(String(b.empleado)));
};

/** Escribe la hoja Cronológico. `lineasEncabezado`: título, entidad, periodo y filtros. */
export const construirHojaCronologica = (ws, hojasData, lineasEncabezado) => {
  const sujetos = agruparMovimientosPorSujeto(hojasData);
  const maxMovimientos = Math.max(0, ...sujetos.map((s) => s.movimientos.length));
  escribirLineasEncabezado(ws, [...lineasEncabezado, `Empleados: ${sujetos.length}`]);

  const FIJAS = ['Empleado', 'Tipo de Sujeto', 'No. de Movimientos'];
  const ancho = CAMPOS_MOVIMIENTO.length;

  // Encabezado en dos renglones: "Movimiento N" agrupando sus campos
  const fila1 = ws.addRow([
    ...FIJAS,
    ...Array.from({ length: maxMovimientos }, (_, i) => [
      `Movimiento ${i + 1}`, ...Array(ancho - 1).fill(''),
    ]).flat(),
  ]);
  const fila2 = ws.addRow([
    ...FIJAS.map(() => ''),
    ...Array.from({ length: maxMovimientos }, () => CAMPOS_MOVIMIENTO.map((c) => c.header)).flat(),
  ]);
  aplicarEncabezado(fila1);
  aplicarEncabezado(fila2);
  FIJAS.forEach((_, idx) => ws.mergeCells(fila1.number, idx + 1, fila2.number, idx + 1));
  for (let i = 0; i < maxMovimientos; i += 1) {
    const inicio = FIJAS.length + i * ancho + 1;
    ws.mergeCells(fila1.number, inicio, fila1.number, inicio + ancho - 1);
  }

  sujetos.forEach((s) => {
    const row = ws.addRow([
      s.empleado,
      s.tipo_de_sujeto,
      s.movimientos.length,
      ...s.movimientos.map((m) => CAMPOS_MOVIMIENTO.map((c) => (
        c.key === 'fecha_movimiento' ? fechaDMY(m[c.key]) : (m[c.key] ?? '')
      ))).flat(),
    ]);
    s.movimientos.forEach((m, i) => {
      const color = m.esAsignacion ? COLOR_ASIGNACION : COLOR_DESASIGNACION;
      for (let j = 0; j < ancho; j += 1) {
        row.getCell(FIJAS.length + i * ancho + j + 1).fill = {
          type: 'pattern', pattern: 'solid', fgColor: { argb: color },
        };
      }
    });
  });

  [42, 18, 12].forEach((w, idx) => {
    const columna = ws.getColumn(idx + 1);
    columna.width = w;
  });
  for (let i = 0; i < maxMovimientos; i += 1) {
    CAMPOS_MOVIMIENTO.forEach((c, j) => {
      const columna = ws.getColumn(FIJAS.length + i * ancho + j + 1);
      columna.width = c.width;
    });
  }
  // Fija la columna Empleado y los encabezados al desplazarse
  // eslint-disable-next-line no-param-reassign
  ws.views = [{ state: 'frozen', xSplit: 1, ySplit: fila2.number }];
};

export default construirHojaCronologica;
